import { TFunction } from 'i18next';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '../../../componentsv2/Typography';
import { transferMethods } from '../../../constants/banking';
import { SupportedFeature } from '../../../types/banking';
import { COLORS } from '../../../themev2';

const TransferMethods = ({
  t,
  supportedFeatures,
  swiftCode,
  defaultExpanded = true,
  hasBorder = true,
}: {
  t: TFunction;
  supportedFeatures: SupportedFeature[];
  swiftCode?: string;
  defaultExpanded?: boolean;
  hasBorder?: boolean;
}) => {
  const isSwiftAvailable =
    swiftCode || supportedFeatures.some((supportedFeature) => supportedFeature.transferMethod === 'SWIFT');
  /**
   * Extracts a list of unique local clearing systems from the supported features.
   * Filters out any null or undefined values before creating the unique list.
   */
  const allLocalClearingSystems: string[] = Array.from(
    new Set(
      supportedFeatures
        .map((supportedFeature) => supportedFeature.localClearingSystem)
        .filter((localClearingSystem): localClearingSystem is string => !!localClearingSystem)
    )
  );
  return (
    <Box
      sx={{
        border: hasBorder ? '1px solid #D7E1F5' : 'none',
        borderRadius: '1em',
        overflow: 'hidden',
      }}
    >
      <Accordion defaultExpanded={defaultExpanded}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
          <Grid container>
            <Grid item xs={12}>
              <Typography
                sx={{ fontWeight: '600', fontSize: '14px', lineHeight: '21px', color: COLORS.gray[1300] }}
              >
                {t('banking:connectedAccountDetails.detailsDrawer.accountCapabilities.title')}
              </Typography>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container rowSpacing={2}>
            <Grid item xs={12}>
              <Grid container rowSpacing={1}>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '21px',
                      color: COLORS.gray[1100],
                    }}
                  >
                    {t('banking:connectedAccountDetails.detailsDrawer.accountCapabilities.acceptFundsVia')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Grid container rowSpacing={1.5}>
                    {allLocalClearingSystems.map((localClearingSystem, index) => {
                      return (
                        <Grid
                          item
                          xs={12}
                          key={index}
                          sx={{ display: 'flex', alignItems: 'center', gap: '1em' }}
                        >
                          <CheckIcon sx={{ color: '#0EA480' }} fontSize="small" />
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: '14px',
                              lineHeight: '21px',
                              color: COLORS.gray[1200],
                            }}
                          >
                            {localClearingSystem && localClearingSystem in transferMethods
                              ? t(
                                  `banking:connectedAccountDetails.detailsDrawer.accountCapabilities.methods.${transferMethods[localClearingSystem].method}`
                                )
                              : localClearingSystem}
                            {localClearingSystem && localClearingSystem in transferMethods && (
                              <>
                                {' '}
                                -{' '}
                                <span style={{ fontWeight: 400 }}>
                                  {t(
                                    `banking:connectedAccountDetails.detailsDrawer.accountCapabilities.times.${transferMethods[localClearingSystem].takes}`
                                  )}
                                </span>
                              </>
                            )}
                          </Typography>
                        </Grid>
                      );
                    })}
                    <Grid
                      item
                      xs={12}
                      key={'swiftTransferMethod'}
                      sx={{ display: 'flex', alignItems: 'center', gap: '1em' }}
                    >
                      {isSwiftAvailable ? (
                        <CheckIcon sx={{ color: '#0EA480' }} fontSize="small" />
                      ) : (
                        <CloseIcon sx={{ color: '#91A2BB' }} fontSize="small" />
                      )}
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '21px',
                          color: isSwiftAvailable ? COLORS.gray[1200] : '#91A2BB',
                        }}
                      >
                        {t(
                          `banking:connectedAccountDetails.detailsDrawer.accountCapabilities.methods.${transferMethods['SWIFT'].method}`
                        )}{' '}
                        -{' '}
                        <span style={{ fontWeight: 400 }}>
                          {t(
                            `banking:connectedAccountDetails.detailsDrawer.accountCapabilities.times.${transferMethods['SWIFT'].takes}`
                          )}
                        </span>
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {supportedFeatures.some((supportedFeature) => !!supportedFeature.localClearingSystem) && (
              <Grid item xs={12}>
                <Grid container rowSpacing={1}>
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '21px',
                        color: COLORS.gray[1100],
                      }}
                    >
                      {t(
                        'banking:connectedAccountDetails.detailsDrawer.accountCapabilities.transferDomesticallyVia'
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container rowSpacing={1.5}>
                      {allLocalClearingSystems.map((localClearingSystem, index) => {
                        return (
                          <Grid
                            item
                            xs={12}
                            key={index}
                            sx={{ display: 'flex', alignItems: 'center', gap: '1em' }}
                          >
                            <CheckIcon sx={{ color: '#0EA480' }} fontSize="small" />
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '21px',
                                color: COLORS.gray[1200],
                              }}
                            >
                              {localClearingSystem && localClearingSystem in transferMethods
                                ? t(
                                    `banking:connectedAccountDetails.detailsDrawer.accountCapabilities.methods.${transferMethods[localClearingSystem].method}`
                                  )
                                : localClearingSystem}
                            </Typography>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container rowSpacing={1}>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '21px',
                      color: COLORS.gray[1100],
                    }}
                  >
                    {t(
                      'banking:connectedAccountDetails.detailsDrawer.accountCapabilities.transferInternationallyVia'
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Grid container rowSpacing={1.5}>
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
                      <CheckIcon sx={{ color: '#0EA480' }} fontSize="small" />
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '21px',
                          color: COLORS.gray[1200],
                        }}
                      >
                        {t(
                          `banking:connectedAccountDetails.detailsDrawer.accountCapabilities.methods.localMethodBasedOnCountry`
                        )}
                      </Typography>
                    </Grid>
                    {isSwiftAvailable && (
                      <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
                        <CheckIcon sx={{ color: '#0EA480' }} fontSize="small" />
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: '14px',
                            lineHeight: '21px',
                            color: COLORS.gray[1200],
                          }}
                        >
                          {t(
                            `banking:connectedAccountDetails.detailsDrawer.accountCapabilities.methods.swift`
                          )}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TransferMethods;
